import { Children, cloneElement, isValidElement, useEffect, useRef, useState, type ReactElement, type ReactNode } from "react";
import { Loading } from "@/components/Loading";
import { Link, useLocation, useNavigate, useNavigationType } from "react-router-dom";
import type { BackOrigin } from "@/hooks/useBackTo";
import {
  FRESH_PAGE_STATE,
  isFreshPageState,
  markReturningTo,
  rememberPageScroll,
  ScrollRootContext,
  scrollTopOf,
} from "@/hooks/useListScroll";
import { ChevronRight, ChevronsUpDown, CircleUser, LogOut, Undo2, UserPlus } from "lucide-react";
import { useAuth } from "@/features/auth/auth";
import { PERM } from "@/features/auth/permissions";
import { FEATURES } from "@/config/featureFlags";
import { users as usersApi, type UserListItem } from "@/features/users/api/users.api";
import blogicLogo from "@/assets/blogic-logo.png";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { SearchField } from "@/components/SearchInput";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

export function AppLayout({ children }: { children: ReactNode }) {
  const { user, stopImpersonating, hasPermission } = useAuth();

  const canAppCampaigns = hasPermission(PERM.appCampaigns);
  const canProducts = hasPermission(PERM.products);
  const canRecurring = hasPermission(PERM.recurring);
  const canSalesCampaigns = hasPermission(PERM.salesCampaigns);
  const canQuotes = hasPermission(PERM.salesOrders);
  const canInvoices = hasPermission(PERM.invoices);
  const canResiduals = hasPermission(PERM.residuals);
  const showSalesSection =
    canProducts || canQuotes || canInvoices || canRecurring || canSalesCampaigns || canResiduals;

  const canUsers = hasPermission(PERM.users);
  const canRoles = hasPermission(PERM.roles);
  const canPartnerForms = hasPermission(PERM.partnerForm);
  const canBranches = hasPermission(PERM.branches);
  const canGroups = hasPermission(PERM.groups);
  const showUsersSection =
    canUsers || canRoles || canPartnerForms || canBranches || canGroups;

  const canImportResiduals = hasPermission(PERM.importsModule);
  const canImportMerchants = hasPermission(PERM.importsModule);
  const canImportPdf = hasPermission(PERM.importsModule);
  const canImportUsers = hasPermission(PERM.importsModule);
  const canTaxRates = hasPermission(PERM.manageModule);
  const canAudit = hasPermission(PERM.manageModule);
  const showManageSection =
    canImportResiduals ||
    canImportMerchants ||
    canImportPdf ||
    canImportUsers ||
    canTaxRates ||
    canAudit;

  const canSettings = hasPermission(PERM.settingsSecrets);

  const location = useLocation();
  const { pathname, search } = location;
  const url = `${pathname}${search}`;
  const navigationType = useNavigationType();
  const contentRef = useRef<HTMLDivElement>(null);
  const lastScroll = useRef({ url, top: 0 });
  const previousPage = useRef(location);

  useEffect(() => {
    const prev = previousPage.current;
    previousPage.current = location;
    if (prev.pathname !== pathname) {
      rememberPageScroll(lastScroll.current.url, lastScroll.current.top);
      const origin = (prev.state as Partial<BackOrigin> | null)?.backTo;
      const isReturn =
        navigationType === "POP" ||
        (!isFreshPageState(location.state) && (origin === url || prev.pathname.startsWith(`${pathname}/`)));
      markReturningTo(isReturn ? url : null);
    }
    contentRef.current?.scrollTo({ top: 0 });
    window.scrollTo({ top: 0 });
  }, [pathname]);

  useEffect(() => {
    const root = contentRef.current;
    const record = () => {
      lastScroll.current = { url, top: scrollTopOf(root) };
    };
    record();
    window.addEventListener("scroll", record, { passive: true });
    root?.addEventListener("scroll", record, { passive: true });
    return () => {
      window.removeEventListener("scroll", record);
      root?.removeEventListener("scroll", record);
    };
  }, [url, user?.userId]);

  return (
    <SidebarProvider>
      <Sidebar collapsible="icon">
        <SidebarHeader>
          <div className="flex items-center gap-2 px-1 py-0.5">
            <img
              src={blogicLogo}
              alt="BLogic Systems"
              className="h-6 w-auto group-data-[collapsible=icon]:hidden"
            />
            <SidebarTrigger className="ml-auto shrink-0" />
          </div>
        </SidebarHeader>

        <SidebarContent className="gap-0 overflow-hidden">
          <ScrollArea className="flex-1">
            <div className="flex flex-col gap-1 py-1">
              <SidebarGroup className="py-1">
                <SidebarGroupLabel>Overview</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    <NavItem to="/" exact icon="dashboard">Dashboard</NavItem>
                    <NavItem to="/merchants" icon="merchants">Merchants</NavItem>
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>

              <SidebarGroup className="py-1">
                <SidebarGroupLabel>Workspace</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    <NavSection label="Onboarding" icon="application" storageKey="sidebar:section:application">
                      <NavItem to="/applications/v2">Application</NavItem>
                      <NavItem to="/onboarding-form">Onboarding Form</NavItem>
                      {FEATURES.equipment && (FEATURES.equipmentForAgents || user?.role !== "USER") && <NavItem to="/equipment">Order Equipment</NavItem>}
                      <NavItem to="/verify-form">Verify Form</NavItem>
                      {FEATURES.applicationCampaigns && canAppCampaigns && (
                        <NavItem to="/manage/application-campaigns">Application Campaigns</NavItem>
                      )}
                    </NavSection>

                    {FEATURES.salesModule && showSalesSection && (
                      <NavSection label="Sales" icon="sales" storageKey="sidebar:section:sales">
                        {canProducts && <NavItem to="/manage/products">Products</NavItem>}
                        {canQuotes && <NavItem to="/quotes?view=orders" view="orders">Quotes</NavItem>}
                        {canInvoices && <NavItem to="/quotes?view=invoices" view="invoices">Invoices</NavItem>}
                        {canRecurring && <NavItem to="/manage/recurring-invoices">Recurring Invoices</NavItem>}
                        {canSalesCampaigns && <NavItem to="/manage/sales-campaigns">Sales Campaign</NavItem>}
                        {canResiduals && <NavItem to="/reports/residuals">Residuals</NavItem>}
                      </NavSection>
                    )}

                    <NavSection label="Support" icon="support" storageKey="sidebar:section:support">
                      <NavItem to="/tickets">Tickets</NavItem>
                    </NavSection>

                    {showUsersSection && (
                      <NavSection label="Users" icon="users" storageKey="sidebar:section:users">
                        {canUsers && <NavItem to="/manage/users">Users</NavItem>}
                        {canRoles && <NavItem to="/manage/roles">Roles</NavItem>}
                        {canPartnerForms && <NavItem to="/manage/partner-form">Partner Form</NavItem>}
                        {canBranches && <NavItem to="/manage/branches">Branches</NavItem>}
                        {canGroups && <NavItem to="/manage/groups">Groups</NavItem>}
                      </NavSection>
                    )}

                    {FEATURES.manageModule && showManageSection && (
                      <NavSection label="Manage" icon="sliders" storageKey="sidebar:section:manage">
                        {canImportResiduals && <NavItem to="/imports/residuals">Import Residuals</NavItem>}
                        {canImportMerchants && <NavItem to="/imports/merchants">Import Merchants</NavItem>}
                        {canImportPdf && <NavItem to="/imports/merchant-pdf">Import Merchant PDF</NavItem>}
                        {canImportUsers && <NavItem to="/imports/users">Import Users</NavItem>}
                        {canTaxRates && <NavItem to="/manage/tax-rates">Tax Rates</NavItem>}
                        {canAudit && <NavItem to="/manage/audit-log">Audit Log</NavItem>}
                      </NavSection>
                    )}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </div>
          </ScrollArea>

          {canSettings && (
            <SidebarGroup className="py-1">
              <SidebarGroupContent>
                <SidebarMenu>
                  <NavItem to="/manage/settings" icon="manage">Settings</NavItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )}
        </SidebarContent>

        <SidebarFooter>
          <AccountMenu />
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>

      <SidebarInset className="bg-slate-50">
        <div className="flex items-center gap-2 border-b border-slate-200 bg-white p-2 md:hidden">
          <SidebarTrigger />
          <img src={blogicLogo} alt="BLogic Systems" className="h-5 w-auto" />
        </div>

        {user?.impersonatedBy && (
          <div className="flex items-center justify-between gap-3 border-b border-amber-300 bg-amber-100 px-6 py-2 text-sm text-amber-900">
            <span>
              You're signed in as <strong>{user.name}</strong>. Acting on behalf of this
              user (started by {user.impersonatedBy}).
            </span>
            <Button
              size="sm"
              onClick={() => stopImpersonating()}
              className="whitespace-nowrap bg-amber-900 text-white hover:bg-amber-950"
            >
              Return to your account
            </Button>
          </div>
        )}

        <div ref={contentRef} key={user?.userId} className="flex-1 overflow-auto p-4 sm:p-6">
          <ScrollRootContext.Provider value={contentRef}>{children}</ScrollRootContext.Provider>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}

function AccountMenu() {
  const { user, signOut, impersonate, stopImpersonating } = useAuth();
  const navigate = useNavigate();
  const { isMobile } = useSidebar();
  const [picker, setPicker] = useState(false);

  const isAdmin = user?.role === "ADMIN";
  const impersonating = !!user?.impersonatedBy;
  const initials =
    (user?.name ?? "?")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase() ?? "")
      .join("") || "?";

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton size="lg" className="data-[state=open]:bg-sidebar-accent">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="border border-amber-200 bg-amber-100 text-sm font-semibold text-amber-700">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{user?.name}</span>
                <span className="truncate text-xs text-sidebar-foreground/70">{user?.email}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={8}
            className="min-w-56"
          >
            <DropdownMenuLabel className="font-normal">
              <div className="grid text-sm">
                <span className="truncate font-medium">{user?.name}</span>
                <span className="truncate text-xs text-muted-foreground">
                  {isAdmin ? "Administrator" : "User"} · {user?.email}
                </span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/me">
                <CircleUser className="size-4" />
                My account
              </Link>
            </DropdownMenuItem>
            {isAdmin && !impersonating && (
              <DropdownMenuItem onClick={() => setPicker(true)}>
                <UserPlus className="size-4" />
                Sign in as…
              </DropdownMenuItem>
            )}
            {impersonating && (
              <DropdownMenuItem
                onClick={async () => { await stopImpersonating(); navigate("/"); }}
                className="text-amber-800 focus:text-amber-900"
              >
                <Undo2 className="size-4" />
                Re-Login Back
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => signOut()}>
              <LogOut className="size-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>

      {picker && (
        <SignInAsModal
          currentUserId={user?.userId}
          onClose={() => setPicker(false)}
          onPick={async (id) => {
            await impersonate(id);
            setPicker(false);
            navigate("/");
          }}
        />
      )}
    </SidebarMenu>
  );
}

const SIGN_IN_AS_PAGE_SIZE = 20;

function SignInAsModal({
  currentUserId,
  onClose,
  onPick,
}: {
  currentUserId?: string;
  onClose: () => void;
  onPick: (userId: string) => Promise<void>;
}) {
  const [list, setList] = useState<UserListItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const keyword = q.trim();
    const timer = setTimeout(() => {
      usersApi.list({ keyword, pageNumber: 1, pageSize: SIGN_IN_AS_PAGE_SIZE, sort: [{ by: "name", dir: "asc" }] })
        .then((p) => {
          if (cancelled) return;
          setList(p.items);
          setTotal(p.totalCount);
          setErr(null);
        })
        .catch((e) => {
          if (!cancelled) setErr(e instanceof Error ? e.message : "Couldn't load users.");
        });
    }, keyword ? 300 : 0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [q]);

  const filtered = (list ?? []).filter((u) => u.id !== currentUserId);

  async function pick(id: string) {
    setBusyId(id);
    setErr(null);
    try {
      await onPick(id);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't sign in as this user.");
      setBusyId(null);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Sign in as another user</DialogTitle>
          <DialogDescription>
            View the portal exactly as they see it. A banner lets you return to your
            own account at any time.
          </DialogDescription>
        </DialogHeader>

        <SearchField
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, email, or #number"
        />
        {err && <p className="text-sm text-destructive">{err}</p>}
        {list === null ? (
          <Loading>Loading…</Loading>
        ) : (
          <div className="max-h-80 divide-y divide-slate-100 overflow-y-auto rounded-md border border-slate-100">
            {filtered.length === 0 ? (
              <p className="px-3 py-3 text-sm text-slate-500">No matching users.</p>
            ) : (
              filtered.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  disabled={busyId !== null}
                  onClick={() => pick(u.id)}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-slate-50 disabled:opacity-50"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm text-slate-800">{u.name}</span>
                    <span className="block truncate text-xs text-slate-500">
                      {u.email}{u.userNumber != null ? ` · #${u.userNumber}` : ""}
                    </span>
                  </span>
                  <span className="whitespace-nowrap text-xs text-brand-600">
                    {busyId === u.id ? "Switching…" : "Sign in →"}
                  </span>
                </button>
              ))
            )}
          </div>
        )}
        {total > filtered.length && (
          <p className="text-xs text-muted-foreground">
            Showing {filtered.length} of {total} — type to search the rest.
          </p>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SectionIcon({ name }: { name: string }) {
  const p = {
    width: 16, height: 16, viewBox: "0 0 24 24", fill: "none",
    stroke: "currentColor", strokeWidth: 1.8,
    strokeLinecap: "round" as const, strokeLinejoin: "round" as const,
    className: "shrink-0", "aria-hidden": true,
  };
  switch (name) {
    case "dashboard":
      return (<svg {...p}><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /></svg>);
    case "merchants":
      return (<svg {...p}><path d="M3 21h18" /><path d="M5 21V7l8-4v18" /><path d="M19 21V11l-6-4" /></svg>);
    case "application":
      return (<svg {...p}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></svg>);
    case "sales":
      return (<svg {...p}><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><path d="M7 7h.01" /></svg>);
    case "support":
      return (<svg {...p}><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" /></svg>);
    case "users":
      return (<svg {...p}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>);
    case "sliders":
      return (<svg {...p}><line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" /><line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" /><line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" /><line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" /></svg>);
    case "manage":
      return (<svg {...p}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>);
    default:
      return null;
  }
}

function NavSection({
  label,
  icon,
  storageKey,
  children,
}: {
  label: string;
  icon: string;
  storageKey: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState<boolean>(() => {
    const v = localStorage.getItem(storageKey);
    return v === null ? true : v === "1";
  });

  return (
    <Collapsible
      asChild
      open={open}
      onOpenChange={(o) => { setOpen(o); localStorage.setItem(storageKey, o ? "1" : "0"); }}
      className="group/collapsible"
    >
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton tooltip={label}>
            <SectionIcon name={icon} />
            <span>{label}</span>
            <ChevronRight className="ml-auto size-4 transition-transform group-data-[state=open]/collapsible:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {Children.map(children, (child) =>
              isValidElement(child)
                ? cloneElement(child as ReactElement<{ sub?: boolean }>, { sub: true })
                : child,
            )}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

function NavItem({
  to,
  exact,
  view,
  icon,
  sub,
  children,
}: {
  to: string;
  exact?: boolean;
  view?: string;
  icon?: string;
  sub?: boolean;
  children: string;
}) {
  const loc = useLocation();
  const path = to.split("?")[0];

  let active: boolean;
  if (view !== undefined) {
    const current = new URLSearchParams(loc.search).get("view") ?? "all";
    active = loc.pathname === path && current === view;
  } else if (exact) {
    active = loc.pathname === path;
  } else {
    active = loc.pathname === path || loc.pathname.startsWith(path + "/");
  }

  if (sub) {
    return (
      <SidebarMenuSubItem>
        <SidebarMenuSubButton asChild isActive={active}>
          <Link to={to} state={FRESH_PAGE_STATE}>
            {icon && <SectionIcon name={icon} />}
            <span>{children}</span>
          </Link>
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
    );
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={active} tooltip={children}>
        <Link to={to} state={FRESH_PAGE_STATE}>
          {icon && <SectionIcon name={icon} />}
          <span>{children}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}
